<template>
	<div class="lending-side-menu-root">
		<div
			class="lending-side-menu"
			:class="{ open: visible, 'pc-menu': isPc }"
			v-show="visible"
		>
			<div v-if="isPc" class="menu-mask" @click="onMaskClose"></div>
			<div class="menu-panel">
				<div v-if="isLogin" class="menu-user-head">
					<div class="menu-uid-row">
						<div class="uid-left">
							<span class="uid-label">{{ $t('lendingMenu.uid') }}</span>
							<span class="uid-value">{{ userUid || '--' }}</span>
							<button
								v-if="userUid"
								type="button"
								class="uid-copy-btn"
								:aria-label="$t('lendingMenu.copyUid')"
								@click.stop="copyUid"
							>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<rect x="5.5" y="5.5" width="8" height="8" rx="1.2" stroke="currentColor" stroke-width="1.2"/>
									<path d="M4.5 10.5h-1a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v1" stroke="currentColor" stroke-width="1.2"/>
								</svg>
							</button>
						</div>
						<div
							class="uid-chip"
							role="button"
							@click.stop="toggleAccount"
						>
							<svg class="uid-chip-switch" width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
								<path d="M2.4 5.6h10.2M10.2 3.8L13.4 5.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M13.6 10.4H3.4M5.8 12.2L2.6 10.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							<span>{{ currentAccountTypeLabel }}</span>
						</div>
					</div>
					<div class="menu-status-row">
						<div
							class="status-tag"
							:class="vipTagClass"
							role="button"
							:aria-label="$t('lendingMenu.vip')"
							@click.stop="openVipInfo"
						>{{ vipLevelLabel }}</div>
						<div
							class="status-tag"
							:class="kycTagClass"
							role="button"
							@click.stop="openIdentity"
						>{{ kycTagLabel }}</div>
					</div>
				</div>
				<div
					v-if="showGetStarted"
					class="menu-item join-entry"
					:class="{ disabled: joinLoading }"
					@click="openGetStarted"
				>
					<span class="menu-icon" v-html="joinIcon"></span>
					<span class="menu-label">{{ $t('lendingMenu.getStarted') }}</span>
				</div>
				<section
					class="menu-section"
					v-for="section in menuSections"
					:key="section.key"
				>
					<div class="menu-section-title">{{ $t(section.titleKey) }}</div>
					<div class="menu-grid">
						<div
							class="grid-item"
							v-for="item in section.items"
							:key="item.key"
							:class="{ active: item.active, 'is-pending': pendingNavKey === item.key }"
							role="button"
							:aria-busy="pendingNavKey === item.key ? 'true' : 'false'"
							@mouseenter="prefetchMenuItem(item)"
							@click="onGridItem(item)"
						>
							<span class="grid-icon">
								<span class="grid-icon-svg" v-html="item.icon"></span>
								<i v-if="item.badge" class="menu-red-dot"></i>
							</span>
							<span class="grid-label">{{ $t(item.labelKey) }}</span>
						</div>
					</div>
				</section>
			</div>

			<Languages :isPc="isPc" :isLanguage="isLanguage" @close="isLanguage = false" />
			<ThemeSheet :visible="isTheme" @close="isTheme = false" />
		</div>

		<VipInfo v-if="vipShow" @hide="vipShow = false" />
		<WithdrawalPinDialog
			:visible="showWithdrawalPinDialog"
			:mode="withdrawalPinDialogMode"
			@close="showWithdrawalPinDialog = false"
			@success="handlePinSetSuccess"
		/>
		<div
			class="confirm-sheet"
			:class="{ 'is-pc': isPc }"
			v-show="showLogoutConfirm"
			@click="closeLogoutConfirm"
		>
			<div class="sheet-panel" @click.stop>
				<div class="sheet-handle" v-if="!isPc" aria-hidden="true"></div>
				<div class="sheet-title">{{ $t('account.logOut') }}</div>
				<p class="sheet-message">{{ $t('account.logoutConfirm') }}</p>
				<div class="sheet-primary" role="button" @click="confirmLogout">{{ $t('account.okay') }}</div>
				<div class="sheet-cancel" role="button" @click="closeLogoutConfirm">{{ $t('account.cancel') }}</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Languages from '@/components/Languages.vue'
import ThemeSheet from '@/components/ThemeSheet.vue'
import WithdrawalPinDialog from '@/components/WithdrawalPinDialog.vue'
import VipInfo from '@/components/VipInfo.vue'
import { WalletUtils } from '@/api/wallet/walletUtils'
import { authorizeLendingAsset, handleAuthorizeError, requireLogin } from '@/utils/lendingGetStarted'
import { openCustomerService } from '@/utils/customerService'
import { getAuthEntryPath } from '@/utils/authRedirect'
import { prefetchMenuRoutes, prefetchRoute } from '@/router/asyncViews'
import { DEMO_BLOCKED_MENU_KEYS } from '@/utils/demoAccount'

const ACTION_ROUTE = {
	goMarket: 'market',
	goTrading: 'trading',
	goNfts: 'nfts',
	goAssets: 'profile',
	goDeposit: 'deposit',
	goWithdraw: 'withdraw',
	goMoneyLog: 'moneyLog',
	goOrders: 'orders',
	goLendingPools: 'lendingPools',
	goLoan: 'loan',
	openIdentity: 'identity',
	goInvite: 'invite',
	goNotification: 'notification'
}

const ICONS = {
	join: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5v13M3.5 10h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`,
	home: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 9.2L10 3.5l6.5 5.7V16a1 1 0 0 1-1 1h-3.6v-4.2H8.1V17H4.5a1 1 0 0 1-1-1V9.2z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>`,
	lendingPools: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7.2" cy="12.6" r="3.1" stroke="currentColor" stroke-width="1.4"/><circle cx="12.8" cy="12.6" r="3.1" stroke="currentColor" stroke-width="1.4"/><circle cx="10" cy="7.4" r="3.1" stroke="currentColor" stroke-width="1.4"/></svg>`,
	market: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.8 13.2L7 8.6L10.4 11.8L17.2 5.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.4 5.2H17.2V9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
	smart: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.4 10A5.6 5.6 0 0 1 13.8 6.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M13.2 3.8v2.6h-2.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M15.6 10A5.6 5.6 0 0 1 6.2 13.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M6.8 16.2v-2.6h2.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10" cy="10" r="1.4" stroke="currentColor" stroke-width="1.4"/></svg>`,
	nfts: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="3.5" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M3.5 13.2L7.2 10.2L10.5 12.8L16.5 7.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8" cy="7.2" r="1.15" stroke="currentColor" stroke-width="1.4"/></svg>`,
	assets: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="5" width="13" height="10" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M3.5 8.5h13" stroke="currentColor" stroke-width="1.4"/><path d="M7 12h2.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	deposit: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5v9.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M6.4 9.4L10 13.2l3.6-3.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 16.2h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	withdraw: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 13.2V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M6.4 7.3L10 3.5l3.6 3.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 16.2h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	moneyLog: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="3.5" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M6.4 7.6h4.2M6.4 10h4.2M6.4 12.4h3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M13.4 7.2v5.6M12.2 8.5c.4-.6 1-.9 1.6-.9.9 0 1.6.5 1.6 1.2 0 .8-.7 1.2-1.6 1.2-.6 0-1.2-.3-1.6-.8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`,
	orders: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="3.5" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.4"/><path d="M6.5 7.5h7M6.5 10h7M6.5 12.5h4.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	language: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="7.2" stroke="currentColor" stroke-width="1.4"/><path d="M3 10h14M10 2.8c2 2.2 3 4.5 3 7.2s-1 5-3 7.2M10 2.8C8 5 7 7.3 7 10s1 5 3 7.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	theme: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="3.2" stroke="currentColor" stroke-width="1.4"/><path d="M10 2.5v1.6M10 15.9v1.6M2.5 10h1.6M15.9 10h1.6M4.7 4.7l1.1 1.1M14.2 14.2l1.1 1.1M4.7 15.3l1.1-1.1M14.2 5.8l1.1-1.1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	customerService: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 4.5h12a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1H8.8L5 15.5V5.5a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M7 8.5h6M7 11h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	loan: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="6" width="14" height="8.5" rx="1.5" stroke="currentColor" stroke-width="1.4"/><circle cx="10" cy="10.25" r="1.8" stroke="currentColor" stroke-width="1.4"/><path d="M5.2 8.2v4.1M14.8 8.2v4.1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	identity: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3.5" y="5" width="13" height="10" rx="1.5" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="9.2" r="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M5.8 13.2c.4-1.2 1.4-1.8 2.2-1.8s1.8.6 2.2 1.8M12 9.2h3.2M12 12h3.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	pin: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4.5" y="9" width="11" height="7.5" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M7 9V6.8a3 3 0 0 1 6 0V9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="10" cy="12.8" r="1" fill="currentColor"/></svg>`,
	invite: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7.2" cy="7.2" r="2.4" stroke="currentColor" stroke-width="1.4"/><path d="M3.5 15.2c.5-2.2 2-3.4 3.7-3.4s3.2 1.2 3.7 3.4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M13.2 7.2h3.6M15 5.4v3.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M12.6 15.2c.4-1.6 1.5-2.6 2.8-2.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	notice: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5c-2.6 0-4.7 2-4.7 4.5v2.4L3.8 13h12.4l-1.5-2.6V8c0-2.5-2.1-4.5-4.7-4.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8.2 15.2c.4 1.1 1.1 1.6 1.8 1.6s1.4-.5 1.8-1.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`,
	logout: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.2 4.5H5.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h2.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M11.2 13.5L15.2 10l-4-3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M15 10H8.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`
}

export default {
	name: 'LendingSideMenu',
	components: { Languages, ThemeSheet, WithdrawalPinDialog, VipInfo },
	props: {
		visible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			isLanguage: false,
			isTheme: false,
			joinLoading: false,
			walletUtils: null,
			vipShow: false,
			showWithdrawalPinDialog: false,
			withdrawalPinDialogMode: 'set',
			showLogoutConfirm: false,
			navBusy: false,
			pendingNavKey: '',
			_didPrefetch: false,
			accountSwitching: false,
			joinIcon: ICONS.join,
			homeIcon: ICONS.home,
			lendingPoolsIcon: ICONS.lendingPools,
			marketIcon: ICONS.market,
			nftsIcon: ICONS.nfts,
			assetsIcon: ICONS.assets,
			ordersIcon: ICONS.orders,
			languageIcon: ICONS.language,
			themeIcon: ICONS.theme,
			customerServiceIcon: ICONS.customerService,
			loanIcon: ICONS.loan,
			identityIcon: ICONS.identity,
			pinIcon: ICONS.pin,
			inviteIcon: ICONS.invite,
			noticeIcon: ICONS.notice,
			logoutIcon: ICONS.logout
		}
	},
	computed: {
		...mapState(['isPc', 'currentLanguage', 'themeMode', 'isLogin', 'userInfo', 'hasNotice', 'hasCsUnread']),
		...mapGetters(['langList', 'langIndex', 'hasWalletTokenAuthorized', 'walletAuthStatusLoaded', 'virtualStatus']),
		isHomeActive() {
			return this.$route.name === 'home'
		},
		isLendingPoolsActive() {
			return this.$route.name === 'lendingPools' || this.$route.name === 'lendingPoolDetail'
		},
		isMarketActive() {
			return this.$route.name === 'market'
		},
		isTradingActive() {
			return this.$route.name === 'trading' || this.$route.name === 'Introduction'
		},
		isNftsActive() {
			return this.$route.name === 'nfts'
		},
		isAssetsActive() {
			return this.$route.name === 'profile'
		},
		isDepositActive() {
			return this.$route.name === 'deposit'
		},
		isWithdrawActive() {
			return this.$route.name === 'withdraw'
		},
		isMoneyLogActive() {
			return this.$route.name === 'moneyLog'
		},
		isOrdersActive() {
			return this.$route.name === 'orders'
		},
		isInviteActive() {
			return this.$route.name === 'invite'
		},
		isNotificationActive() {
			return this.$route.name === 'notification'
		},
		isIdentityActive() {
			return this.$route.name === 'identity'
		},
		hasWithdrawalPin() {
			return !!(this.userInfo && this.userInfo.hasWithdrawalPin)
		},
		isLoanActive() {
			return this.$route.name === 'loan'
		},
		currentLangName() {
			const item = (this.langList || [])[this.langIndex]
			if (item && item.name) return item.name
			const found = (this.langList || []).find((i) => i.value === this.currentLanguage)
			return found ? found.name : (this.currentLanguage || 'en')
		},
		currentThemeName() {
			const map = {
				auto: 'lendingTheme.auto',
				light: 'lendingTheme.light',
				dark: 'lendingTheme.dark'
			}
			return this.$t(map[this.themeMode] || map.auto)
		},
		userUid() {
			const id = this.userInfo && this.userInfo.id
			if (id === null || id === undefined || id === '') return ''
			return String(id)
		},
		userLevel() {
			const level = this.userInfo && this.userInfo.level
			const n = Number(level)
			if (!Number.isFinite(n) || n < 0) return 0
			return Math.min(4, Math.floor(n))
		},
		vipLevelLabel() {
			return this.$t('lendingMenu.vipLevelTag', { level: this.userLevel })
		},
		vipTagClass() {
			return `is-vip-${this.userLevel}`
		},
		kycTagLabel() {
			const status = this.userInfo && this.userInfo.idefinderStatus
			if (status === 1 || status === '1') return this.$t('lendingMenu.kycVerified')
			if (status === 0 || status === '0') return this.$t('lendingMenu.kycPending')
			return this.$t('lendingMenu.kycUnverified')
		},
		kycTagClass() {
			const status = this.userInfo && this.userInfo.idefinderStatus
			if (status === 1 || status === '1') return 'is-kyc-verified'
			if (status === 0 || status === '0') return 'is-kyc-pending'
			return 'is-kyc-unverified'
		},
		currentAccountTypeLabel() {
			return this.virtualStatus == 2 ? this.$t('account.demo') : this.$t('account.live')
		},
		showGetStarted() {
			return false
		},
		menuSections() {
			const trade = [
				{ key: 'home', labelKey: 'lendingMenu.home', icon: ICONS.home, active: this.isHomeActive, action: 'goHome' },
				{ key: 'market', labelKey: 'lendingMenu.market', icon: ICONS.market, active: this.isMarketActive, action: 'goMarket' },
				{ key: 'trading', labelKey: 'lendingMenu.smartTrading', icon: ICONS.smart, active: this.isTradingActive, action: 'goTrading' },
				{ key: 'orders', labelKey: 'lendingMenu.orders', icon: ICONS.orders, active: this.isOrdersActive, action: 'goOrders' }
			]
			const wallet = [
				{ key: 'assets', labelKey: 'lendingMenu.assets', icon: ICONS.assets, active: this.isAssetsActive, action: 'goAssets' },
				{ key: 'deposit', labelKey: 'lendingMenu.deposit', icon: ICONS.deposit, active: this.isDepositActive, action: 'goDeposit' },
				{ key: 'withdraw', labelKey: 'lendingMenu.withdraw', icon: ICONS.withdraw, active: this.isWithdrawActive, action: 'goWithdraw' },
				{ key: 'moneyLog', labelKey: 'lendingMenu.moneyLog', icon: ICONS.moneyLog, active: this.isMoneyLogActive, action: 'goMoneyLog' }
			]
			const sections = [
				{ key: 'trade', titleKey: 'lendingMenu.sectionTrade', items: trade },
				{ key: 'wallet', titleKey: 'lendingMenu.sectionWallet', items: wallet },
				{
					key: 'finance',
					titleKey: 'lendingMenu.sectionFinance',
					items: [
						{ key: 'lendingPools', labelKey: 'lendingMenu.lendingPools', icon: ICONS.lendingPools, active: this.isLendingPoolsActive, action: 'goLendingPools' },
						{ key: 'loan', labelKey: 'lendingMenu.loan', icon: ICONS.loan, active: this.isLoanActive, action: 'goLoan' }
					]
				}
			]
			if (this.isLogin) {
				sections.push({
					key: 'account',
					titleKey: 'lendingMenu.sectionAccount',
					items: [
						{ key: 'identity', labelKey: 'account.accountVerification', icon: ICONS.identity, active: this.isIdentityActive, action: 'openIdentity' },
						{ key: 'pin', labelKey: 'account.WithdrawalPIN', icon: ICONS.pin, action: 'openWithdrawalPin' },
						{ key: 'invite', labelKey: 'account.inviteFriends', icon: ICONS.invite, active: this.isInviteActive, action: 'goInvite' },
						{ key: 'notice', labelKey: 'account.notification', icon: ICONS.notice, active: this.isNotificationActive, action: 'goNotification', badge: this.hasNotice }
					]
				})
			}
			sections.push({
				key: 'more',
				titleKey: 'lendingMenu.sectionMore',
				items: [
					{ key: 'nfts', labelKey: 'lendingMenu.nfts', icon: ICONS.nfts, active: this.isNftsActive, action: 'goNfts' },
					{ key: 'service', labelKey: 'lendingMenu.customerService', icon: ICONS.customerService, action: 'openService', badge: this.isLogin && this.hasCsUnread }
				]
			})
			const settings = [
				{ key: 'language', labelKey: 'lendingMenu.language', icon: ICONS.language, action: 'openLanguage' },
				{ key: 'theme', labelKey: 'lendingMenu.theme', icon: ICONS.theme, action: 'openTheme' }
			]
			if (this.isLogin) {
				settings.push({ key: 'logout', labelKey: 'account.logOut', icon: ICONS.logout, action: 'openLogout' })
			}
			sections.push({ key: 'settings', titleKey: 'lendingMenu.sectionSettings', items: settings })
			if (Number(this.virtualStatus) === 2) {
				return sections
					.map((section) => ({
						...section,
						items: section.items.filter((item) => !DEMO_BLOCKED_MENU_KEYS.includes(item.key))
					}))
					.filter((section) => section.items.length)
			}
			return sections
		}
	},
	watch: {
		visible(val) {
			document.body.style.overflow = val ? 'hidden' : ''
			if (val) {
				this.schedulePrefetch()
				if (this.isLogin) {
					this.$store.dispatch('FETCH_UNREAD_BADGES')
				}
			} else {
				this.isLanguage = false
				this.isTheme = false
				if (!this.navBusy) this.pendingNavKey = ''
			}
		}
	},
	beforeDestroy() {
		document.body.style.overflow = ''
	},
	methods: {
		onMaskClose() {
			if (this.navBusy) return
			this.$emit('close')
		},
		schedulePrefetch() {
			if (this._didPrefetch) return
			this._didPrefetch = true
			const run = () => prefetchMenuRoutes()
			if (typeof requestIdleCallback === 'function') {
				requestIdleCallback(run, { timeout: 600 })
			} else {
				setTimeout(run, 80)
			}
		},
		prefetchMenuItem(item) {
			if (!item) return
			const name = ACTION_ROUTE[item.action]
			if (name) prefetchRoute(name).catch(() => {})
		},
		onGridItem(item) {
			if (!item || this.navBusy) return
			const fn = this[item.action]
			if (typeof fn === 'function') fn()
		},
		async goToRoute(name, { key, query, already } = {}) {
			if (this.navBusy) return
			const isAlready = already != null ? already : this.$route.name === name
			if (isAlready) {
				this.$emit('close')
				return
			}
			this.navBusy = true
			this.pendingNavKey = key || name
			try {
				prefetchRoute(name)
				await this.$router.push(query ? { name, query } : { name })
			} catch (e) {
				// ignore duplicate / cancelled navigation
			} finally {
				this.navBusy = false
				this.pendingNavKey = ''
				this.$emit('close')
			}
		},
		goHome() {
			this.goToRoute('home', { key: 'home', already: this.$route.name === 'home' })
		},
		goLendingPools() {
			this.goToRoute('lendingPools', { key: 'lendingPools' })
		},
		goMarket() {
			this.goToRoute('market', { key: 'market' })
		},
		goTrading() {
			this.goToRoute('trading', { key: 'trading' })
		},
		goNfts() {
			this.goToRoute('nfts', { key: 'nfts' })
		},
		goAssets() {
			this.goToRoute('profile', { key: 'assets' })
		},
		goDeposit() {
			this.goToRoute('deposit', { key: 'deposit' })
		},
		goWithdraw() {
			this.goToRoute('withdraw', { key: 'withdraw' })
		},
		goMoneyLog() {
			this.goToRoute('moneyLog', { key: 'moneyLog' })
		},
		goOrders() {
			this.goToRoute('orders', { key: 'orders' })
		},
		goInvite() {
			this.goToRoute('invite', { key: 'invite' })
		},
		goNotification() {
			this.goToRoute('notification', { key: 'notice' })
		},
		openIdentity() {
			if (Number(this.virtualStatus) === 2) {
				this.$message.warning(this.$t('20120'))
				return
			}
			this.goToRoute('identity', { key: 'identity' })
		},
		openWithdrawalPin() {
			this.$emit('close')
			this.withdrawalPinDialogMode = this.hasWithdrawalPin ? 'change' : 'set'
			this.showWithdrawalPinDialog = true
		},
		handlePinSetSuccess() {
			this.showWithdrawalPinDialog = false
			if (this.userInfo) {
				this.$store.commit('set_userInfo', {
					...this.userInfo,
					hasWithdrawalPin: true
				})
			}
			this.$store.dispatch('GET_USER_INFO')
		},
		openLogout() {
			this.$emit('close')
			this.showLogoutConfirm = true
		},
		closeLogoutConfirm() {
			this.showLogoutConfirm = false
		},
		confirmLogout() {
			this.showLogoutConfirm = false
			this.$store.dispatch('loginOut').then(() => {
				this.$router.push(getAuthEntryPath())
			})
		},
		toggleAccount() {
			if (this.accountSwitching) return
			if (Number(this.virtualStatus) !== 2 && this.userInfo && this.userInfo.virtuallyStatus != 1) {
				this.$message.warning(this.$t('yanShiZhangHuYiJinYong'))
				return
			}
			this.accountSwitching = true
			this.$store.dispatch('switchAccount').then((res) => {
				if (res && res.code == '200' && res.result) {
					const isDemo = Number(res.result.userType) === 2
					this.$message.success(this.$t(isDemo ? 'yiQieHuanWeiXuNiZhangHu' : 'yiQieHuanWeiZhenShiZhangHu'))
					window.location.reload()
					return
				}
				this.$message.warning(this.$t((res && res.message) || '20120'))
			}).catch(() => {
				this.$message.warning(this.$t('20120'))
			}).finally(() => {
				this.accountSwitching = false
			})
		},
		switchAccount() {
			this.toggleAccount()
		},
		goLoan() {
			this.goToRoute('loan', { key: 'loan' })
		},
		openService() {
			this.$emit('close')
			openCustomerService(this)
		},
		openVipInfo() {
			this.vipShow = true
		},
		openLanguage() {
			this.isLanguage = true
		},
		openTheme() {
			this.isTheme = true
		},
		async copyUid() {
			const text = this.userUid
			if (!text) return
			try {
				if (navigator.clipboard && navigator.clipboard.writeText) {
					await navigator.clipboard.writeText(text)
				} else {
					const textarea = document.createElement('textarea')
					textarea.value = text
					textarea.setAttribute('readonly', '')
					textarea.style.position = 'fixed'
					textarea.style.opacity = '0'
					document.body.appendChild(textarea)
					textarea.select()
					document.execCommand('copy')
					document.body.removeChild(textarea)
				}
				this.$message({
					message: this.$t('toast.copySuccess'),
					type: 'success'
				})
			} catch (e) {
				console.error('Copy UID failed', e)
			}
		},
		async openGetStarted() {
			if (this.joinLoading) return
			this.$emit('close')
			this.joinLoading = true
			try {
				if (!requireLogin(this)) return
				if (!this.walletUtils) {
					this.walletUtils = new WalletUtils()
				}
				await authorizeLendingAsset(this, 'USDT', { walletUtils: this.walletUtils })
			} catch (e) {
				handleAuthorizeError(this, e)
			} finally {
				this.joinLoading = false
			}
		}
	}
}
</script>

<style lang="scss" scoped>
/* 移动端：全屏菜单 */
.lending-side-menu {
	position: fixed;
	inset: 0;
	z-index: 10;
	background: var(--ld-bg, #fff);
	color: var(--ld-text, #111);
	padding-top: var(--ld-nav-space, 0.8rem);
	padding-bottom: var(--ld-safe-bottom, 0px);
	box-sizing: border-box;
	overflow-x: hidden;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.menu-panel {
	padding: 0.12rem 0.16rem 0.4rem;
	display: flex;
	flex-direction: column;
	min-height: 100%;
	box-sizing: border-box;
}

.menu-user-head {
	padding: 0.14rem 0.16rem 0.16rem;
	margin-bottom: 0.2rem;
	border: 1px solid var(--ld-border, #ececec);
	border-radius: 0.16rem;
	background: var(--ld-bg-muted, #f7f7f7);
}

.menu-uid-row {
	--uid-row-h: 0.36rem;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.1rem;
	height: var(--uid-row-h);
	font-size: 0.18rem;
	color: var(--ld-text-secondary, #888);
}

.uid-left {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.08rem;
	min-width: 0;
	height: var(--uid-row-h);
	flex: 1;
}

.uid-label,
.uid-value {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	height: var(--uid-row-h);
	margin: 0;
	padding: 0;
	line-height: 1;
	text-box-trim: trim-both;
	text-box-edge: cap alphabetic;
}

.uid-label {
	font-weight: 500;
	color: var(--ld-text-secondary, #888);
}

.uid-value {
	flex-shrink: 1;
	min-width: 0;
	color: var(--ld-text, #111);
	font-variant-numeric: tabular-nums;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.uid-copy-btn {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	width: var(--uid-row-h);
	height: var(--uid-row-h);
	padding: 0;
	margin: 0;
	border: none;
	border-radius: 0.06rem;
	background: transparent;
	color: var(--ld-text-secondary, #888);
	cursor: pointer;
	line-height: 0;
	font-size: 0;
	font-family: inherit;
	appearance: none;
	-webkit-tap-highlight-color: transparent;

	&:hover,
	&:active {
		color: var(--ld-text, #111);
		background: var(--ld-bg-active, #f2f2f2);
	}

	svg {
		display: block;
		width: 0.16rem;
		height: 0.16rem;
	}
}

.uid-chip {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.06rem;
	flex-shrink: 0;
	box-sizing: border-box;
	height: var(--uid-row-h);
	padding: 0 0.12rem 0 0.1rem;
	border-radius: 999px;
	background: var(--ld-bg-active, #f2f2f2);
	color: var(--ld-text, #111);
	font-size: 0.14rem;
	font-weight: 600;
	line-height: 1;
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;

	&:active {
		outline: none;
		box-shadow: none;
		opacity: 0.88;
	}
}

.uid-chip-switch {
	display: block;
	flex-shrink: 0;
	width: 0.2rem;
	height: 0.2rem;
}

.menu-status-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.08rem;
	margin-top: 0.1rem;
}

.status-tag {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 0.26rem;
	padding: 0 0.1rem;
	border-radius: 999px;
	font-size: 0.12rem;
	font-weight: 600;
	line-height: 1;
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;

	&:active {
		outline: none;
		opacity: 0.88;
	}

	&.is-vip-0 {
		background: #f3f4f6;
		color: #6b7280;
	}

	&.is-vip-1 {
		background: #fef3c7;
		color: #b45309;
	}

	&.is-vip-2 {
		background: #e0f2fe;
		color: #0369a1;
	}

	&.is-vip-3 {
		background: #ffedd5;
		color: #c2410c;
	}

	&.is-vip-4 {
		background: #ede9fe;
		color: #6d28d9;
	}

	&.is-kyc-verified {
		background: #dcfce7;
		color: #15803d;
	}

	&.is-kyc-pending {
		background: #fef9c3;
		color: #a16207;
	}

	&.is-kyc-unverified {
		background: #fee2e2;
		color: #b91c1c;
	}
}

@media (hover: hover) {
	.uid-chip:hover {
		background: var(--ld-bg-muted, #f7f7f7);
	}

	.status-tag:hover {
		opacity: 0.88;
	}
}

[data-lending-theme="dark"] .status-tag {
	&.is-vip-0 {
		background: #2a2a2a;
		color: #a3a3a3;
	}

	&.is-vip-1 {
		background: #3d2e12;
		color: #fbbf24;
	}

	&.is-vip-2 {
		background: #0c2d48;
		color: #7dd3fc;
	}

	&.is-vip-3 {
		background: #3b1f0a;
		color: #fdba74;
	}

	&.is-vip-4 {
		background: #2e1065;
		color: #c4b5fd;
	}

	&.is-kyc-verified {
		background: #14532d;
		color: #86efac;
	}

	&.is-kyc-pending {
		background: #422006;
		color: #fde047;
	}

	&.is-kyc-unverified {
		background: #450a0a;
		color: #fca5a5;
	}
}

.lending-side-menu.pc-menu .menu-user-head {
	padding: 14px 16px 16px;
	margin-bottom: 20px;
	border-radius: 16px;
}

.lending-side-menu.pc-menu .menu-uid-row {
	--uid-row-h: 36px;
	font-size: 18px;
}

.lending-side-menu.pc-menu .uid-copy-btn {
	border-radius: 6px;

	svg {
		width: 16px;
		height: 16px;
	}
}

.lending-side-menu.pc-menu .uid-chip {
	gap: 6px;
	padding: 0 12px 0 10px;
	font-size: 14px;
}

.lending-side-menu.pc-menu .uid-chip-switch {
	width: 20px;
	height: 20px;
}

.lending-side-menu.pc-menu .menu-status-row {
	margin-top: 10px;
	gap: 8px;
}

.lending-side-menu.pc-menu .status-tag {
	height: 26px;
	padding: 0 10px;
	font-size: 12px;
}

/* PC：左侧抽屉 + 遮罩 */
.lending-side-menu.pc-menu {
	background: transparent;
	padding: 0;
	overflow: hidden;
	display: flex;
}

.lending-side-menu.pc-menu .menu-mask {
	position: absolute;
	inset: 0;
	background: var(--ld-overlay, rgba(17, 17, 17, 0.28));
}

.lending-side-menu.pc-menu .menu-panel {
	position: relative;
	z-index: 1;
	width: 360px;
	max-width: 86vw;
	height: 100%;
	margin: 0;
	padding: 80px 16px 24px;
	background: var(--ld-bg-card, #fff);
	border-right: 1px solid var(--ld-border, #ececec);
	box-shadow: var(--ld-menu-shadow, 4px 0 24px rgba(0, 0, 0, 0.06));
	overflow-x: hidden;
	overflow-y: auto;
	animation: lending-menu-slide-in 0.22s ease-out;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}

.lending-side-menu.pc-menu .menu-item {
	padding: 12px 14px;
	font-size: 15px;
	gap: 10px;
	border-radius: 12px;
}

.lending-side-menu.pc-menu .menu-icon {
	width: 22px;
	height: 22px;

	::v-deep svg {
		width: 22px;
		height: 22px;
	}
}

.lending-side-menu.pc-menu .menu-item.join-entry {
	.menu-icon,
	.menu-label {
		height: 22px;
		line-height: 22px;
	}
}

@keyframes lending-menu-slide-in {
	from {
		transform: translateX(-100%);
	}
	to {
		transform: translateX(0);
	}
}

.menu-item.join-entry {
	display: flex;
	align-items: center;
	gap: 0.08rem;
	padding: 0.14rem 0.16rem;
	border-radius: 0.14rem;
	font-size: 0.16rem;
	font-weight: 600;
	color: var(--ld-accent, #5347e0);
	cursor: pointer;
	user-select: none;
	-webkit-tap-highlight-color: transparent;

	.menu-icon,
	.menu-label {
		height: 0.24rem;
		line-height: 0.24rem;
	}

	&.disabled {
		opacity: 0.6;
		pointer-events: none;
	}
}

.menu-icon {
	width: 0.24rem;
	height: 0.24rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: inherit;
	flex-shrink: 0;
	line-height: 0;

	::v-deep svg {
		width: 0.24rem;
		height: 0.24rem;
		display: block;
	}
}

.menu-label {
	flex: 1;
	line-height: 0.24rem;
	display: flex;
	align-items: center;
	min-width: 0;
}

.menu-section {
	margin-bottom: 0.22rem;
}

.menu-section-title {
	padding: 0 0.02rem 0.1rem;
	font-size: 0.15rem;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	line-height: 1.2;
	color: var(--ld-text, #111);
}

.menu-grid {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 0.08rem;
	width: 100%;
	min-width: 0;
}

.grid-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
	min-width: 0;
	padding: 0.08rem 0.04rem;
	border-radius: 0.16rem;
	background: transparent;
	color: var(--ld-text, #111);
	cursor: pointer;
	user-select: none;
	box-sizing: border-box;
	-webkit-tap-highlight-color: transparent;

	&:active {
		outline: none;
		box-shadow: none;
		transform: none;
		background: transparent !important;
		border-radius: 0.16rem !important;
	}

	&.is-pending {
		pointer-events: none;
	}

	&.active .grid-icon,
	&.is-pending .grid-icon {
		background: var(--ld-accent, #5347e0);
		color: var(--ld-accent-text, #fff);
	}
}

.grid-icon {
	position: relative;
	flex: 0 0 auto;
	width: 0.52rem;
	height: 0.52rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 0.08rem;
	border-radius: 0.14rem;
	background: var(--ld-bg-muted, #f7f7f7);
	color: var(--ld-accent, #5347e0);
	line-height: 0;

	::v-deep svg {
		width: 0.26rem;
		height: 0.26rem;
		display: block;
	}
}

.grid-icon-svg {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 0;
}

.menu-red-dot {
	position: absolute;
	top: -0.02rem;
	right: -0.02rem;
	width: 0.08rem;
	height: 0.08rem;
	border-radius: 50%;
	background: var(--ld-danger, #ef4444);
	pointer-events: none;
}

.grid-label {
	width: 100%;
	min-width: 0;
	font-size: 0.14rem;
	line-height: 1.3;
	font-weight: 500;
	text-align: center;
	white-space: normal;
	overflow-wrap: anywhere;
	word-break: break-word;
}

.lending-side-menu.pc-menu .menu-section {
	margin-bottom: 22px;
}

.lending-side-menu.pc-menu .menu-section-title {
	padding: 0 2px 10px;
	font-size: 15px;
	font-weight: 700;
}

.lending-side-menu.pc-menu .menu-grid {
	gap: 8px;
}

.lending-side-menu.pc-menu .grid-item {
	padding: 8px 4px;
	border-radius: 16px;

	&:active {
		border-radius: 16px !important;
	}
}

.lending-side-menu.pc-menu .grid-icon {
	width: 52px;
	height: 52px;
	margin-bottom: 8px;
	border-radius: 14px;

	::v-deep svg {
		width: 26px;
		height: 26px;
	}
}

.lending-side-menu.pc-menu .menu-red-dot {
	top: -2px;
	right: -2px;
	width: 8px;
	height: 8px;
}

.lending-side-menu.pc-menu .grid-label {
	font-size: 14px;
}

.confirm-sheet {
	position: fixed;
	inset: 0;
	z-index: 120;
	background: var(--ld-overlay, rgba(17, 17, 17, 0.36));
	display: flex;
	align-items: flex-end;
	justify-content: center;
	box-sizing: border-box;
}

.confirm-sheet.is-pc {
	align-items: center;
	padding: 24px;
}

.sheet-panel {
	width: 100%;
	max-width: 520px;
	background: var(--ld-bg-card, #fff);
	color: var(--ld-text, #111);
	border-radius: 20px 20px 0 0;
	padding: 10px 16px calc(16px + var(--ld-safe-bottom));
	box-sizing: border-box;
}

.confirm-sheet.is-pc .sheet-panel {
	width: 400px;
	max-width: 92vw;
	border-radius: 16px;
	padding: 20px 16px 16px;
	box-shadow: var(--ld-shadow);
}

.sheet-handle {
	width: 36px;
	height: 4px;
	border-radius: 999px;
	background: var(--ld-handle, #d8d8d8);
	margin: 6px auto 12px;
}

.sheet-title {
	font-size: 16px;
	font-weight: 600;
	text-align: center;
	padding: 4px 12px 8px;
}

.sheet-message {
	margin: 0 0 16px;
	font-size: 14px;
	line-height: 1.5;
	color: var(--ld-text-secondary, #888);
	text-align: center;
}

.sheet-primary {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 48px;
	padding: 0 20px;
	border: none;
	border-radius: 999px;
	background: var(--ld-accent, #5347e0);
	color: var(--ld-accent-text, #fff);
	font-family: inherit;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	box-sizing: border-box;

	&:active {
		opacity: 0.88;
		background: var(--ld-accent, #5347e0) !important;
		border-radius: 999px !important;
	}
}

.sheet-cancel {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	margin-top: 10px;
	min-height: 44px;
	padding: 0 14px;
	border: 1px solid var(--ld-border, #e5e5e5);
	border-radius: 999px;
	background: var(--ld-btn-ghost-bg, #fff);
	color: var(--ld-btn-ghost-text, #111);
	font-family: inherit;
	font-weight: 600;
	cursor: pointer;
	box-sizing: border-box;

	&:active {
		opacity: 0.88;
		background: var(--ld-bg-muted, #f7f7f7) !important;
	}
}
</style>
